"use client";
import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { normalizeCourseName } from '@/utils/normalizeCourseName'
import { useLanguageStore } from "@/providers/useLanguageStore";
import { useFAQs } from "@/hooks/useFAQs";
import { FiArrowUpRight } from "react-icons/fi";

interface FAQ {
  title: string;
  answer: string;
}

interface FaqListProps {
  course?: string;
  career?: string;
  classroom?: {
    carreer?: string;
    course?: string;
  };
  className?: string;
}
function FaqItem({
  item,
  isOpen,
  onToggle,
  index,
}: {
  item: FAQ;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="flex items-start justify-between w-full py-6 text-left transition-opacity hover:opacity-80"
      >
        <div className="flex flex-1 gap-6">
          <span className="text-lg font-bold text-gray-400">
            {index < 9 ? `0${index}` : index}
          </span>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
                }`}
            >
              {item.answer && (
                <p
                  className="leading-relaxed text-gray-600"
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 ml-4">
          <div className="flex items-center justify-center w-8 h-8 text-white bg-red-500 rounded-full">
            {isOpen ? <X size={16} /> : <Plus size={16} />}
          </div>
        </div>
      </button>
    </div>
  );
}
export default function FaqList({ course, career, classroom, className }: FaqListProps) {
  const { translations } = useLanguageStore();
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [openItems, setOpenItems] = useState<Set<number>>(new Set([0, 1, 2, 3, 4]));
  const { faqs: classroomFaqs, loading: classroomLoading, error: classroomError } = useFAQs(classroom);
  const [legacyFaqs, setLegacyFaqs] = useState<FAQ[]>([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyError, setLegacyError] = useState<string | null>(null);
  useEffect(() => {
    if (classroom) return;
    const fetchLegacyFAQs = async () => {
      if (!course && !career) return;
      setLegacyLoading(true);
      setLegacyError(null);
      try {
        const params = new URLSearchParams();
        if (course) {
          const normalizedCourse = normalizeCourseName(course)
            .replace(/-\d+-meses$/, '')
            .replace(/-/g, ' ');
          params.append("course", normalizedCourse);
        }
        if (course) params.append("course", course);
        if (career) params.append("career", career);
        const res = await fetch(`/api/faq?${params.toString()}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || translations.faqError);
        }
        const data = await res.json();
        if (Array.isArray(data)) {
          setLegacyFaqs(data);
        } else {
          setLegacyFaqs([]);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        setLegacyError(message || translations.unknownError);
      } finally {
        setLegacyLoading(false);
      }
    };
    fetchLegacyFAQs();
  }, [course, career, classroom, translations.faqError, translations.unknownError]);
  const faqs = classroom ? classroomFaqs : legacyFaqs;
  const loading = classroom ? classroomLoading : legacyLoading;
  const error = classroom ? classroomError : legacyError;
  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen);
    setOpenItems(new Set());
  };
  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };
  if (loading) {
    return (
      <section className={`w-full max-w-4xl px-4 py-1 mx-auto ${className || ''}`}>
        <div className="mb-12">
          <h2 className="mb-4 text-4xl font-bold text-red-600">
            {translations.frequentlyAskedQuestions}
          </h2>
          <p className="text-lg text-gray-500">
            {translations.faqDescription}
          </p>
        </div>
        <div className="bg-white shadow-sm rounded-xl">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="border-b border-gray-200 animate-pulse">
              <div className="flex items-start justify-between w-full py-6">
                <div className="flex flex-1 gap-6">
                  <div className="w-8 h-6 bg-gray-300 rounded"></div>
                  <div className="flex-1">
                    <div className="w-3/4 h-6 mb-2 bg-gray-300 rounded"></div>
                    <div className="w-full h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (error) {
    return <p className="font-medium text-red-600">{error}</p>;
  }
  if (faqs.length === 0) {
    return null;
  }
  return (
    <section className={`w-full max-w-4xl px-4 py-12 mx-auto ${className || ''}`}>
      <div className="mb-12">
        <h2 className="text-3xl lg:text-4xl font-bold text-red-600 mb-4 font-archivo flex items-center">
          <FiArrowUpRight className="text-red-600 w-8 h-8 flex-shrink-0" />
          {translations.frequentlyAskedQuestions}
        </h2>
        <p className="text-lg text-gray-500">
          {translations.faqDescription}
        </p>
      </div>
      <div className="bg-white shadow-sm rounded-xl">
        <div className="border-b border-gray-200">
          <button
            onClick={toggleAccordion}
            className="flex items-start justify-between w-full py-6 text-left transition-opacity hover:opacity-80"
          >
            <div className="flex flex-1 gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900">{translations.viewAllFaqs}</h3>
              </div>
            </div>
            <div className="flex-shrink-0 ml-4">
              <div className="flex items-center justify-center w-8 h-8 text-white bg-red-500 rounded-full">
                {isAccordionOpen ? <X size={16} /> : <Plus size={16} />}
              </div>
            </div>
          </button>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${isAccordionOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
        >
          {faqs.map((item, index) => (
            <FaqItem
              key={index}
              item={item}
              isOpen={openItems.has(index)}
              onToggle={() => toggleItem(index)}
              index={index + 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}